/**
 * AI Chat REST API client.
 *
 * Replaces WebSocket-based CRUD operations with REST calls to the AI Chat API
 * (API Gateway + Lambda, co-located with the agent in us-west-2).
 *
 * All requests include the Cognito ID token in the Authorization header.
 */

import { Auth } from 'aws-amplify'

/** Base URL for the AI Chat API. Local, dev and prod all use the same production API. */
const CHAT_API_BASE =
  process.env.REACT_APP_CHAT_API_URL || 'https://api.omicsai.biobam.com'

/** Conversation list item returned by GET /conversations. */
export interface ConversationSummary {
  id: string
  title: string
  sessionId?: string
  createdAt: string
  updatedAt: string
}

/** Message returned by GET /conversations/{id}/messages. */
export interface ChatMessageRecord {
  /** Server row id ("user-<id>" / "assistant-<id>") — stable across refetches, unlike a client id. */
  messageId?: string
  role: string
  content: string
  /** `complete` (default) or `interrupted` when the user stopped the run. */
  status?: string
  steps?: string // JSON string of v2 timeline `{ schema_version, entries }`, or null
  tokenUsage?: string // JSON string of TokenUsage, or null
  contextUsage?: string // JSON string of context window fill metrics, or null
  contextCompacted?: boolean
  modelUsed?: string // model identifier, or null
  /** AgentCore display id (e.g. omicsai_bioagent_v4-19); persisted for assistant turns. */
  agentRuntimeLabel?: string | null
  /** JSON string of string[] (tool names), or a native array from older Dynamo rows. */
  toolsUsed?: unknown
  /** JSON string of string[] (follow-up prompts), or a native array. Persisted for assistant turns. */
  suggestedFollowups?: unknown
  /** JSON string of KB source rows, or a native array. */
  kbSources?: unknown
  /** JSON string of web source rows, or a native array. */
  internetSources?: unknown
  /** `files_changed` ledger of the turn (file-ops contract §3): a native array or its JSON string. */
  filesChanged?: unknown
  /** Full change count of a compacted `files_changed` (also sent as `files_changed_total`). */
  filesChangedTotal?: unknown
  /** True when `files_changed` was compacted or dropped (also `files_changed_truncated`). */
  filesChangedTruncated?: unknown
  timestamp: string
}

/** Response from GET /conversations/{id}/messages. */
export interface ConversationMessagesResponse {
  conversationId: string
  sessionId: string
  messages: ChatMessageRecord[]
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

async function getAuthHeaders(): Promise<Record<string, string>> {
  const session = await Auth.currentSession()
  const token = session.getIdToken().getJwtToken()
  return {
    Authorization: token,
    'Content-Type': 'application/json',
  }
}

async function apiFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const headers = await getAuthHeaders()
  const response = await fetch(`${CHAT_API_BASE}${path}`, {
    ...init,
    headers: { ...headers, ...(init?.headers ?? {}) },
  })
  if (!response.ok) {
    const body = await response.text()
    throw new Error(`Chat API ${init?.method ?? 'GET'} ${path} failed (${response.status}): ${body}`)
  }
  return response.json() as Promise<T>
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/** List all conversations for the authenticated user (sorted newest first). */
export async function listConversations(): Promise<ConversationSummary[]> {
  const data = await apiFetch<{ conversations: ConversationSummary[] }>('/conversations')
  return data.conversations
}

/** Load messages for a conversation. */
export async function loadConversationMessages(
  conversationId: string
): Promise<ConversationMessagesResponse> {
  return apiFetch<ConversationMessagesResponse>(`/conversations/${conversationId}/messages`)
}

/** Rename a conversation. */
export async function renameConversation(
  conversationId: string,
  title: string
): Promise<{ conversationId: string; title: string }> {
  return apiFetch<{ conversationId: string; title: string }>(
    `/conversations/${conversationId}`,
    { method: 'PATCH', body: JSON.stringify({ title }) }
  )
}

/**
 * Opt a conversation in or out of server-side continuation: when a run it is waiting on finishes,
 * the agent runs the follow-up turn even with no browser open. Billed like any other turn, which
 * is why it is off by default and the user chooses.
 */
export async function setCloudContinue(
  conversationId: string,
  cloudContinue: boolean
): Promise<boolean> {
  const data = await apiFetch<{ conversationId: string; cloudContinue?: boolean }>(
    `/conversations/${conversationId}`,
    { method: 'PATCH', body: JSON.stringify({ cloudContinue }) }
  )
  return Boolean(data.cloudContinue)
}

/** Delete a conversation and all its messages. */
export async function deleteConversation(conversationId: string): Promise<void> {
  await apiFetch<{ deleted: boolean }>(`/conversations/${conversationId}`, { method: 'DELETE' })
}

// ---------------------------------------------------------------------------
// Pending cloud runs
// ---------------------------------------------------------------------------

/** A cloud run one of this user's conversations is waiting on, as the server sees it. */
export interface PendingRunRecord {
  runId: string
  conversationId: string
  tool?: string | null
  /** `wes` (a WES SessionId) or `mcp-task` (an `mcp-<uuid>` mini-job). */
  lane?: string
  /** Last observed run state, e.g. RUNNING / COMPLETE / EXECUTOR_ERROR. */
  state?: string
  /** Set once the run reached a terminal state; null while it is still going. */
  resolvedAt?: string | null
  /** `complete` | `failed` | `canceled`, present only alongside `resolvedAt`. */
  status?: string | null
  /** `none` | `claimed` | `done` | `failed` — whether a follow-up turn is spoken for. */
  continuation?: string
  createdAt?: string
}

/**
 * Open runs plus those resolved in the last day. The server re-checks live state as it answers, so
 * this is what clears a tag after a missed notification — or a closed laptop.
 */
export async function fetchPendingRuns(): Promise<PendingRunRecord[]> {
  const data = await apiFetch<{ runs: PendingRunRecord[] }>('/pending-runs')
  return data.runs ?? []
}

/**
 * Try to win the right to run this run's follow-up turn. Every device competes for the same claim,
 * so a run that finishes while two browsers are open produces one turn, not two (and one charge).
 */
export async function claimContinuation(runId: string): Promise<boolean> {
  const data = await apiFetch<{ claimed: boolean }>(
    `/pending-runs/${encodeURIComponent(runId)}/claim-continuation`,
    { method: 'POST', body: JSON.stringify({}) }
  )
  return Boolean(data.claimed)
}

/**
 * Stop tracking a run server-side. The strip reads from the server, so a purely local removal is
 * undone by the next poll — the row is still open, so the tag comes straight back.
 */
export async function forgetPendingRun(runId: string): Promise<void> {
  await apiFetch<{ deleted: boolean }>(`/pending-runs/${encodeURIComponent(runId)}`, {
    method: 'DELETE',
  })
}

/** Release a claim once the follow-up turn has been dispatched. */
export async function releaseContinuation(runId: string): Promise<void> {
  await apiFetch<{ released: boolean }>(
    `/pending-runs/${encodeURIComponent(runId)}/claim-continuation`,
    { method: 'POST', body: JSON.stringify({ state: 'done' }) }
  )
}
